"use client";
import { useT } from "@/context/I18nContext";
import { useState } from "react";
import { Drawer } from "vaul";

const LANGUAGES = [
	{ code: "ro", label: "Română", flag: "https://flagcdn.com/w40/ro.png" },
	{ code: "en", label: "English", flag: "https://flagcdn.com/w40/gb.png" },
	{ code: "it", label: "Italiano", flag: "https://flagcdn.com/w40/it.png" },
	{ code: "fr", label: "Français", flag: "https://flagcdn.com/w40/fr.png" },
	{ code: "de", label: "Deutsch", flag: "https://flagcdn.com/w40/de.png" },
];

const CURRENCIES = [
	{ code: "RON", label: "Leu românesc (RON)", symbol: "RON", accent: "#2da44e" },
	{ code: "EUR", label: "Euro (EUR)", symbol: "€", accent: "#0074d9" },
];

export default function RegionSwitcher() {
	const { locale, setLocale, currency, setCurrency } = useT();
	const [open, setOpen] = useState(false);

	const currentLang = LANGUAGES.find((l) => l.code === locale) ?? LANGUAGES[0];
	const currentCur = CURRENCIES.find((c) => c.code === currency) ?? CURRENCIES[0];

	return (
		<Drawer.Root open={open} onOpenChange={setOpen}>
			<style>{`
				.region-trigger {
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					gap: 2px;
					background: none;
					border: none;
					padding: 0;
					cursor: pointer;
					color: inherit;
				}
				.region-drawer-content {
					background: #fff;
					display: flex;
					flex-direction: column;
					border-radius: 20px 20px 0 0;
					margin-top: -1px;
					height: auto;
					max-height: 75vh;
					position: fixed;
					bottom: 0;
					left: 0;
					right: 0;
					z-index: 100000;
					padding-bottom: calc(env(safe-area-inset-bottom) + 16px);
				}
				.region-drawer-overlay {
					position: fixed;
					inset: 0;
					background: rgba(0, 0, 0, 0.45);
					z-index: 100000;
				}
				.region-drawer-body {
					padding: 6px 18px 4px;
					overflow-y: auto;
				}
				.region-drawer-title {
					font-size: 15px;
					font-weight: 700;
					color: #111;
					margin: 4px 0 14px;
				}
				.region-section-label {
					font-size: 12px;
					font-weight: 700;
					color: #939ba8;
					text-transform: uppercase;
					letter-spacing: 0.4px;
					margin-bottom: 8px;
				}
				.region-option-list {
					display: flex;
					flex-direction: column;
					gap: 2px;
					margin-bottom: 20px;
				}
				.region-option {
					display: flex;
					align-items: center;
					gap: 12px;
					width: 100%;
					padding: 12px;
					background: transparent;
					border: none;
					border-radius: 12px;
					cursor: pointer;
					font-size: 15px;
					text-align: left;
					-webkit-tap-highlight-color: transparent;
					transition: background 0.15s;
				}
				.region-option:active {
					background: #f0f2f5;
				}
				.region-option.selected {
					background: #eef5ff;
				}
			`}</style>

			<Drawer.Trigger asChild>
				<button className='region-trigger' aria-label='Limbă și monedă'>
					<img
						src={currentLang.flag}
						width='18'
						height='13'
						alt={currentLang.label}
						style={{ borderRadius: "2px", display: "block" }}
					/>
					<span style={{ fontSize: "10px", fontWeight: 700, color: "inherit", lineHeight: 1 }}>
						{currentCur.code}
					</span>
				</button>
			</Drawer.Trigger>

			<Drawer.Portal>
				<Drawer.Overlay className='region-drawer-overlay' />
				<Drawer.Content className='region-drawer-content'>
					<Drawer.Handle />
					<div className='region-drawer-body'>
						<Drawer.Title className='region-drawer-title'>Limbă și monedă</Drawer.Title>

						<div className='region-section-label'>Limbă</div>
						<div className='region-option-list'>
							{LANGUAGES.map((lang) => (
								<button
									key={lang.code}
									className={`region-option${locale === lang.code ? " selected" : ""}`}
									onClick={() => {
										setLocale(lang.code);
										setOpen(false);
									}}
									style={{
										fontWeight: locale === lang.code ? 600 : 400,
										color: locale === lang.code ? "#0074d9" : "#333",
									}}>
									<img
										src={lang.flag}
										width='24'
										height='16'
										alt={lang.label}
										style={{ borderRadius: "2px", display: "block" }}
									/>
									<span>{lang.label}</span>
									{locale === lang.code && (
										<svg
											width='14'
											height='14'
											viewBox='0 0 24 24'
											fill='none'
											stroke='#0074d9'
											strokeWidth='3'
											strokeLinecap='round'
											strokeLinejoin='round'
											style={{ marginLeft: "auto" }}>
											<polyline points='20 6 9 17 4 12' />
										</svg>
									)}
								</button>
							))}
						</div>

						<div className='region-section-label'>Monedă</div>
						<div className='region-option-list'>
							{CURRENCIES.map((cur) => (
								<button
									key={cur.code}
									className={`region-option${currency === cur.code ? " selected" : ""}`}
									onClick={() => {
										setCurrency(cur.code);
										setOpen(false);
									}}
									style={{
										fontWeight: currency === cur.code ? 600 : 400,
										color: currency === cur.code ? "#0074d9" : "#333",
									}}>
									<span
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											minWidth: "26px",
											height: "26px",
											padding: "0 6px",
											fontSize: "12px",
											fontWeight: 700,
											borderRadius: "50%",
											background: cur.accent,
											color: "#fff",
											lineHeight: 1,
										}}>
										{cur.symbol}
									</span>
									<span>{cur.label}</span>
									{currency === cur.code && (
										<svg
											width='14'
											height='14'
											viewBox='0 0 24 24'
											fill='none'
											stroke='#0074d9'
											strokeWidth='3'
											strokeLinecap='round'
											strokeLinejoin='round'
											style={{ marginLeft: "auto" }}>
											<polyline points='20 6 9 17 4 12' />
										</svg>
									)}
								</button>
							))}
						</div>
					</div>
				</Drawer.Content>
			</Drawer.Portal>
		</Drawer.Root>
	);
}
